import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { UnifiedButton } from '../../components/unified';
import { PageContainer } from '../../components/unified';

/**
 * Resend API Test Page
 * Tests if RESEND_API_KEY is properly configured in Supabase
 */
const ResendApiTest = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  const testResendConnection = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Test the send-resend-email edge function
      const { data, error: functionError } = await supabase?.functions?.invoke('send-resend-email', {
        body: {
          emailType: 'ACCOUNT_CONFIRMATION',
          data: {
            userEmail: testEmail,
            userName: 'Test User',
            platformName: 'Recrutees',
            confirmationUrl: 'https://www.recrutees.com/verify-email?token=test123'
          }
        }
      });

      if (functionError) {
        throw functionError;
      }

      if (data?.success) {
        setResult({
          status: 'SUCCESS',
          message: 'Email sent successfully!',
          messageId: data?.messageId,
          emailType: data?.emailType,
          details: 'The Resend API key is properly configured and working.'
        });
      } else {
        throw new Error(data?.error || 'Unknown error occurred');
      }
    } catch (err) {
      console.error('Resend test error:', err);
      setError({
        status: 'ERROR',
        message: err?.message || 'Failed to send test email',
        details: err?.message?.includes('RESEND_API_KEY') 
          ? 'The RESEND_API_KEY environment variable is not configured in Supabase.' :'There was an error sending the email. Check the console for details.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">📧 Resend API Test</h1>
            <p className="text-lg text-gray-600">
              Test if your RESEND_API_KEY is properly configured in Supabase
            </p>
          </div>

          {/* Test Card */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="space-y-6">
              {/* Email Input */}
              <div>
                <label htmlFor="testEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Test Email Address
                </label>
                <input
                  type="email"
                  id="testEmail"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e?.target?.value)}
                  placeholder="your-email@example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={loading}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Enter your email address to receive a test confirmation email
                </p>
              </div>

              {/* Test Button */}
              <UnifiedButton
                onClick={testResendConnection}
                disabled={loading || !testEmail}
                className="w-full"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending Test Email...
                  </span>
                ) : (
                  '🚀 Send Test Email'
                )}
              </UnifiedButton>

              {/* Success Result */}
              {result && (
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">
                        ✅ {result?.status}
                      </h3>
                      <p className="text-green-700 mb-2">{result?.message}</p>
                      <p className="text-sm text-green-600 mb-3">{result?.details}</p>
                      {result?.messageId && (
                        <div className="bg-white rounded p-3 border border-green-200">
                          <p className="text-xs text-gray-600 mb-1">Message ID:</p>
                          <p className="text-sm font-mono text-gray-800 break-all">{result?.messageId}</p>
                        </div>
                      )}
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm font-semibold text-blue-800 mb-2">📬 Check Your Inbox</p>
                        <p className="text-sm text-blue-700">
                          A test confirmation email has been sent to <strong>{testEmail}</strong>. 
                          Please check your inbox (and spam folder) to verify delivery.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Result */}
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">
                        ❌ {error?.status}
                      </h3>
                      <p className="text-red-700 mb-2">{error?.message}</p>
                      <p className="text-sm text-red-600 mb-3">{error?.details}</p>
                      
                      {error?.message?.includes('RESEND_API_KEY') && (
                        <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <p className="text-sm font-semibold text-yellow-800 mb-2">🔧 How to Fix:</p>
                          <ol className="text-sm text-yellow-700 space-y-2 list-decimal list-inside">
                            <li>Go to your Supabase Dashboard</li>
                            <li>Navigate to Settings → Edge Functions</li>
                            <li>Add environment variable: <code className="bg-yellow-100 px-2 py-1 rounded">RESEND_API_KEY</code></li>
                            <li>Paste your Resend API key (starts with <code className="bg-yellow-100 px-2 py-1 rounded">re_</code>)</li>
                            <li>Restart the edge functions</li>
                          </ol>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-blue-800 mb-3">ℹ️ What This Test Does:</h3>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Calls the <code className="bg-blue-100 px-2 py-1 rounded">send-resend-email</code> edge function</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Verifies the RESEND_API_KEY environment variable is set</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Sends a real test email to your specified address</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Returns the Resend message ID if successful</span>
                  </li>
                </ul>
              </div>

              {/* Configuration Status */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">📋 Configuration Checklist:</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-center">
                    <span className="mr-2">✅</span>
                    <span>Edge function <code className="bg-gray-100 px-2 py-1 rounded">send-resend-email</code> is deployed</span>
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">{result ? '✅' : '⏳'}</span>
                    <span>RESEND_API_KEY is configured in Supabase</span>
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">{result ? '✅' : '⏳'}</span>
                    <span>Email delivery is working</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Need help? Check the <a href="/help-center" className="text-purple-600 hover:text-purple-700 underline">Help Center</a> or contact support.</p>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ResendApiTest;